import { motion } from 'framer-motion';
import { forwardRef } from 'react';

const ArrowIconSvg = () => {
  return (
    <svg className="icon-brand" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.3335 8H12.6668" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 3.33333L12.6667 8L8 12.6667" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

  );
};

const TertiaryButtonArrow = () => {
  return (
    <motion.div
      className="relative overflow-hidden ml-3xs w-2 h-2">
      <motion.div className="flex items-center absolute"
        variants={{
          rest: { x: -16 },
          hover: { x: 0 },
        }}
        transition={{
          duration: 0.3,
          ease: [0.5, 0.01, 0.44, 0.99],
        }}
      >
        <ArrowIconSvg />
        <ArrowIconSvg />
      </motion.div>
    </motion.div>
  );
};

type TertiaryButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDragStart'
  | 'onDrag'
  | 'onDragEnd'
> & {
  /**
   * When provided, controls the variant state (e.g. from parent hover). 
   * Use "hover" to trigger the hover animation when parent is hovered. 
   */
  animate?: 'rest' | 'hover';
  /**
   * When true, the button sets no variant props of its own and instead
   * inherits "rest"/"hover" from the nearest motion parent (e.g. a card
   * with whileHover="hover"). This is required for parent-driven hover:
   * framer-motion blocks variant propagation through components that set
   * initial/animate/whileHover. Takes precedence over `animate`.
   */
  inheritVariants?: boolean;
};

const TertiaryButton = forwardRef<HTMLButtonElement, TertiaryButtonProps>(
  ({ className, children, animate, inheritVariants = false, ...props }, ref) => {
    return (
      <motion.button
        ref={ref}
        {...(inheritVariants
          ? {}
          : {
              initial: 'rest',
              animate,
              whileHover: animate === undefined ? 'hover' : undefined,
            })}
        className={className}
        {...props}
      >
        {children}
        <TertiaryButtonArrow />
      </motion.button>
    );
  }
);

TertiaryButton.displayName = 'TertiaryButton';

export { TertiaryButton, TertiaryButtonArrow };
